import { css, cva } from '@styled-system/css';
import { Eye, Layers, Play } from 'lucide-react';
import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { AnalysisNiche } from '@/features/analysisPageNiches/types';
import type { AnalysisPlatform } from '@/shared/api/types/generated';
import { VestiaireLockedButton } from '@/shared/components/analysis/VestiaireLockedChip';
import { Button } from '@/shared/components/ui/Button';
import { DataTable, type DataTableColumn } from '@/shared/components/ui/DataTable';
import { Tooltip } from '@/shared/components/ui/Tooltip';
import { computeNicheRowScore, getNicheScoreTier } from '@/shared/utils/analysis/nicheScore';
import { hasPlatformRelaunchCriteria } from '@/shared/utils/analysis/platform';

// ============================================================================
// Tableau des niches — DataTable DS (pattern AchatsTable d'Autocop).
// Props-driven, réutilisable pour l'analyse vendeur. Tri par défaut :
// % de vente décroissant (la métrique n°1 des utilisateurs).
// ============================================================================

const nicheCell = css({
  display: 'flex',
  alignItems: 'center',
  gap: '4',
  minWidth: 0,
});

const nicheImg = css({
  width: '28px',
  height: '28px',
  borderRadius: 'md',
  objectFit: 'cover',
  bg: 'surface.sunken',
  flexShrink: 0,
});

const nicheImgFallback = css({
  width: '28px',
  height: '28px',
  borderRadius: 'md',
  bg: 'surface.sunken',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'text.tertiary',
  flexShrink: 0,
});

const nicheNameStack = css({
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5',
  minWidth: 0,
});

const nicheName = css({
  fontSize: 'bodySm',
  fontWeight: 'medium',
  color: 'text.primary',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

const nichePath = css({
  fontSize: 'micro',
  color: 'text.tertiary',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

const monoCell = css({
  fontFamily: 'mono',
  fontSize: 'bodySm',
  color: 'text.primary',
});

const mutedMonoCell = css({
  fontFamily: 'mono',
  fontSize: 'bodySm',
  color: 'text.secondary',
});

// % de vente : neutre (blanc semibold) — gris non-gras quand c'est mauvais.
const soldPctCell = cva({
  base: {
    fontFamily: 'mono',
    fontSize: 'bodySm',
    fontWeight: 'semibold',
    color: 'text.primary',
  },
  variants: {
    tier: {
      cold: { color: 'text.tertiary', fontWeight: 'regular' },
      normal: {},
    },
  },
  defaultVariants: { tier: 'normal' },
});

const soldPctTier = (pct: number | null | undefined): 'cold' | 'normal' =>
  pct == null || pct < 10 ? 'cold' : 'normal';

// Score en petit tag coloré — se distingue des colonnes de chiffres bruts.
const scoreCell = cva({
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '1',
    paddingX: '3',
    paddingY: '0.5',
    borderRadius: 'full',
    borderWidth: '1px',
    borderStyle: 'solid',
    fontFamily: 'mono',
    fontSize: 'caption',
    fontWeight: 'bold',
    whiteSpace: 'nowrap',
  },
  variants: {
    tier: {
      poor: { bg: 'error.soft', color: 'error.default', borderColor: 'error.border' },
      average: { bg: 'warning.soft', color: 'warning.default', borderColor: 'warning.border' },
      good: { bg: 'success.soft', color: 'success.default', borderColor: 'success.border' },
    },
  },
});

const scoreOutOf = css({
  fontFamily: 'mono',
  fontSize: 'micro',
  fontWeight: 'regular',
  opacity: 0.7,
});

const formatMono = (value: number | null | undefined, digits = 0, suffix = ''): string =>
  value == null ? '—' : `${value.toFixed(value < 1 && value > 0 ? 2 : digits)}${suffix}`;

const formatHours = (hours: number | null | undefined): string => {
  if (hours == null) return '—';
  const days = Math.floor(hours / 24);
  const rest = Math.round(hours % 24);
  if (days === 0) return `${rest}h`;
  return rest > 0 ? `${days}j ${rest}h` : `${days}j`;
};

const splitNicheName = (niche: AnalysisNiche): { label: string; path?: string } => {
  if (niche.type !== 'category' || !niche.niche.includes('>')) return { label: niche.niche };
  const segments = niche.niche.split('>').map((s) => s.trim());
  return { label: segments[segments.length - 1] ?? niche.niche, path: segments.slice(0, -1).join(' › ') };
};

// ============================================================================

export interface NichesTableProps {
  niches: AnalysisNiche[];
  loading?: boolean;
  /** Recherche texte (filtre global du DataTable). */
  globalFilter?: string;
  /** 'vendor' : pas de score, volumes affichés au mois (ventes + ajouts). */
  variant?: 'product' | 'vendor';
  onViewSales?: (niche: AnalysisNiche) => void;
  /** Lance une nouvelle recherche ciblée sur cette niche (bouton play). */
  onLaunchSearch?: (niche: AnalysisNiche) => void;
  /**
   * Plateforme de l'ANALYSE : seul le champ de relance de cette plateforme compte. `null` =
   * encore inconnue (analyse en chargement) : bouton masqué.
   */
  platform: AnalysisPlatform | null;
  /**
   * Relance verrouillée par le plan (analyse Vestiaire sans accès VC) : bouton grisé + cadenas +
   * infobulle d'abonnement ; le clic n'appelle jamais `onLaunchSearch`, il affiche la notification
   * d'abonnement (`VestiaireLockedButton`).
   */
  launchLocked?: boolean;
}

const DAYS_PER_MONTH = 30.4;

export const NichesTable: FC<NichesTableProps> = ({
  niches,
  loading,
  globalFilter,
  variant = 'product',
  onViewSales,
  onLaunchSearch,
  platform,
  launchLocked = false,
}) => {
  const { t } = useTranslation();

  // Tri par défaut % vente desc — le tri interactif des colonnes prend le
  // relais dès que l'utilisateur clique un header.
  const sortedNiches = useMemo(() => [...niches].sort((a, b) => (b.soldPct ?? -1) - (a.soldPct ?? -1)), [niches]);

  const columns: DataTableColumn<AnalysisNiche>[] = useMemo(
    () => [
      {
        id: 'niche',
        header: t('niches.colNiche'),
        size: 320,
        cell: ({ row }) => {
          const { label, path } = splitNicheName(row.original);
          return (
            <div className={nicheCell}>
              {row.original.imageUrl ? (
                <img src={row.original.imageUrl} alt="" className={nicheImg} />
              ) : (
                <span className={nicheImgFallback}>
                  <Layers size={13} strokeWidth={1.75} />
                </span>
              )}
              <div className={nicheNameStack}>
                <span className={nicheName}>{label}</span>
                {path && <span className={nichePath}>{path}</span>}
              </div>
            </div>
          );
        },
      },
      {
        id: 'soldPct',
        header: t('niches.colSoldPct'),
        size: 110,
        cell: ({ row }) => (
          <span className={soldPctCell({ tier: soldPctTier(row.original.soldPct) })}>
            {row.original.soldPct == null ? '—' : `${Math.round(row.original.soldPct)}%`}
          </span>
        ),
        enableSorting: true,
        sortingFn: (a, b) => (a.original.soldPct ?? -1) - (b.original.soldPct ?? -1),
      },
      {
        id: 'timeToSell',
        header: t('niches.colTimeToSell'),
        size: 120,
        cell: ({ row }) => <span className={mutedMonoCell}>{formatHours(row.original.timeToSellHours)}</span>,
        enableSorting: true,
        sortingFn: (a, b) => (a.original.timeToSellHours ?? Infinity) - (b.original.timeToSellHours ?? Infinity),
      },
      variant === 'vendor'
        ? {
            id: 'soldPerMonth',
            header: t('niches.colSoldPerMonth'),
            size: 110,
            cell: ({ row }) => (
              <span className={mutedMonoCell}>
                {row.original.soldPerDay == null ? '—' : formatMono(row.original.soldPerDay * DAYS_PER_MONTH, 0)}
              </span>
            ),
            enableSorting: true,
            sortingFn: (a, b) => (a.original.soldPerDay ?? -1) - (b.original.soldPerDay ?? -1),
          }
        : {
            id: 'soldPerDay',
            header: t('niches.colSoldPerDay'),
            size: 110,
            cell: ({ row }) => <span className={mutedMonoCell}>{formatMono(row.original.soldPerDay, 1)}</span>,
            enableSorting: true,
            sortingFn: (a, b) => (a.original.soldPerDay ?? -1) - (b.original.soldPerDay ?? -1),
          },
      // Vendeur : les ajouts au mois (rythme de sourcing du profil)
      ...(variant === 'vendor'
        ? [
            {
              id: 'addedPerMonth',
              header: t('niches.colAddedPerMonth'),
              size: 110,
              cell: ({ row }) => (
                <span className={mutedMonoCell}>
                  {row.original.addedPerDay == null ? '—' : formatMono(row.original.addedPerDay * DAYS_PER_MONTH, 0)}
                </span>
              ),
              enableSorting: true,
              sortingFn: (a, b) => (a.original.addedPerDay ?? -1) - (b.original.addedPerDay ?? -1),
            } satisfies DataTableColumn<AnalysisNiche>,
          ]
        : []),
      {
        id: 'avgPrice',
        header: t('niches.colAvgPrice'),
        size: 120,
        cell: ({ row }) => (
          <span className={monoCell}>
            {row.original.avgPrice == null ? '—' : `${row.original.avgPrice.toFixed(2).replace('.', ',')} €`}
          </span>
        ),
        enableSorting: true,
        sortingFn: (a, b) => (a.original.avgPrice ?? -1) - (b.original.avgPrice ?? -1),
      },
      {
        id: 'productCount',
        header: t('niches.colProducts'),
        size: 130,
        cell: ({ row }) => <span className={mutedMonoCell}>{formatMono(row.original.productCount)}</span>,
        enableSorting: true,
        sortingFn: (a, b) => a.original.productCount - b.original.productCount,
      },
      ...(variant === 'product'
        ? [
            {
              id: 'score',
              header: t('niches.colScore'),
              size: 100,
              cell: ({ row }) => {
                const score = computeNicheRowScore(row.original);
                return (
                  <span className={scoreCell({ tier: getNicheScoreTier(score) })}>
                    {score ?? '—'}
                    <span className={scoreOutOf}>/100</span>
                  </span>
                );
              },
              enableSorting: true,
              sortingFn: (a, b) => (computeNicheRowScore(a.original) ?? -1) - (computeNicheRowScore(b.original) ?? -1),
            } satisfies DataTableColumn<AnalysisNiche>,
          ]
        : []),
      {
        id: 'actions',
        header: '',
        size: 110,
        cell: ({ row }) => (
          <div className={css({ display: 'flex', alignItems: 'center', gap: '2', justifyContent: 'flex-end' })}>
            {onViewSales && (
              <Tooltip label={t('niches.viewSales')}>
                <span>
                  <Button
                    variant="secondary"
                    size="sm"
                    icon={<Eye size={13} strokeWidth={1.75} />}
                    onClick={(e) => {
                      e.stopPropagation();
                      onViewSales(row.original);
                    }}
                    aria-label={t('niches.viewSales')}
                  />
                </span>
              </Tooltip>
            )}
            {/* Zoom masqué sans le champ de relance de LA plateforme de l'analyse (cache ancien,
                analyse vendeur, plateforme pas encore connue) — pas de reconstruction de secours */}
            {onLaunchSearch &&
              platform !== null &&
              hasPlatformRelaunchCriteria(row.original, platform) &&
              (launchLocked ? (
                <VestiaireLockedButton
                  variant="primary"
                  size="sm"
                  iconSize={12}
                  iconStrokeWidth={2}
                  ariaLabel={t('niches.launchSearch')}
                />
              ) : (
                <Tooltip label={t('niches.launchSearch')}>
                  <span>
                    <Button
                      variant="primary"
                      size="sm"
                      icon={<Play size={12} strokeWidth={2} />}
                      onClick={(e) => {
                        e.stopPropagation();
                        onLaunchSearch(row.original);
                      }}
                      aria-label={t('niches.launchSearch')}
                    />
                  </span>
                </Tooltip>
              ))}
          </div>
        ),
      },
    ],
    [t, variant, onViewSales, onLaunchSearch, platform, launchLocked],
  );

  return (
    <DataTable
      data={sortedNiches}
      columns={columns}
      getRowId={(niche) => `${niche.type}-${niche.niche}`}
      globalFilter={globalFilter}
      loading={loading}
      emptyTitle={t('niches.emptyTitle')}
      emptyHint={t('niches.emptyHint')}
      onRowClick={onViewSales ? (niche) => onViewSales(niche) : undefined}
    />
  );
};
